import { type Client } from 'braintree-web/modules/client';
import isEmpty from 'lodash/isEmpty';
import { createSelector } from 'reselect';

import { LEVER_PURCHASE_SHOW_PAYMENT_OPTIONS } from 'Lib/ConfigService/levers';
import { GlobalStateType } from 'Lib/reduxType';
import { createKeySelector } from 'Lib/reduxUtils';
import { getLeverValue } from 'Lib/utils/getLeverValue';

import { DEFAULT_CURRENCY, DEFAULT_PRICE } from 'Store/entities/constants';
import { PLATFORM_TYPE_GIFT_CARD } from 'Store/entities/products.schema';
import type { PaymentType, Product, PurchaseType } from 'Store/entities/products.types';
import {
  getHasIncentiveIntroPriceProduct,
  getIncentiveIntroPriceProduct,
} from 'Store/incentives/selectors';
import {
  PAYMENT_TYPE_CARD,
  PAYMENT_TYPE_CREDIT_CARD,
  PAYMENT_TYPE_PAYPAL,
  subscriptionPaymentMethodMap,
} from 'Store/purchase/constants';

import { PURCHASE_METHOD_CC, PURCHASE_METHOD_PAYPAL } from 'Store/sparks/lib/constants';

import { selectProducts } from '../entities/products.selectors';
import {
  findProductPaymentTypeField,
  getProductFieldByPaymentType,
} from '../entities/products.utils';
import type {
  Card,
  PaymentMethod,
  PaymentMethodType,
  PurchaseFormData,
  SubscriptionPaymentMethodType,
  Upsells,
} from './types';

/**
 * Gets profile namespace from the state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {PurchaseState} profile state
 */
export const selectPurchase = createKeySelector('purchase');

/**
 * Gets the ID of the product currently in the cart
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} product ID in cart
 */
export const getCartProductId = createSelector(
  selectPurchase,
  (purchase) => purchase.cart || purchase.purchaseInfo?.product_id
);

/**
 * Selects the product currently in the cart.
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Product} product entity currently in the cart
 */
export const getCartProduct = createSelector(
  getCartProductId,
  selectProducts,
  getHasIncentiveIntroPriceProduct,
  getIncentiveIntroPriceProduct,
  (id, products, hasIncentiveProduct, incentiveProduct): Product | undefined =>
    hasIncentiveProduct ? incentiveProduct ?? undefined : products[id]
);

/**
 * Gets the source of the paywall from which we land on the checkout page
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} source of paywall
 */
export const getFromPaywall = createSelector(selectPurchase, (purchase) => purchase.from);

/**
 * Gets if boost should be auto started after purchase from the top-level state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} if boost should be auto started
 */
export const getShouldAutoStartBoost = createSelector(
  selectPurchase,
  (purchase) => purchase.shouldAutoStartBoost
);

/**
 * Gets if compound boost should be auto started after purchase from the top-level state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return if compound boost should be auto started
 */
export const getShouldAutoStartCompoundBoost = createSelector(
  selectPurchase,
  (purchase): boolean => purchase.shouldAutoStartCompoundBoost
);

/**
 * Gets if current rec should rewind after purchase from the top-level state
 *
 * @param  {GlobalStateType} state - top-level state
 * @return if rec should rewind
 */
export const getShouldAutoRewind = createSelector(
  selectPurchase,
  (purchase): boolean => purchase.shouldAutoRewind
);

/**
 * Gets selected payment method for Analytics
 *
 * @param  {GlobalStateType} state - top-level state
 * @return selected payment method
 */
export const getPaymentMethod = createSelector(
  selectPurchase,
  (purchase): string => purchase.paymentMethod
);

/**
 * Gets selected payment type for checkout page
 * Note: The incentive product does not have paymentMethods provided
 * TODO: Remove hasIncentiveIntroPriceProduct check once the backend supports multiple payment options for the incentive
 *
 * @param  {GlobalStateType} state - top-level state
 * @return selected payment type, if available
 */
export const getPaymentType = createSelector(
  getPaymentMethod,
  getHasIncentiveIntroPriceProduct,
  (paymentMethod, hasIncentiveIntroPriceProduct): PaymentType | undefined => {
    if (hasIncentiveIntroPriceProduct) {
      return;
    }
    return {
      [PURCHASE_METHOD_CC]: PAYMENT_TYPE_CREDIT_CARD,
      [PURCHASE_METHOD_PAYPAL]: PAYMENT_TYPE_PAYPAL,
    }[paymentMethod] as PaymentType | undefined;
  }
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return currency of cart product, defaulted to {@link DEFAULT_CURRENCY}.
 */
export const getCartProductCurrency = createSelector(
  getCartProduct,
  getPaymentType,
  (product, paymentType): string =>
    getProductFieldByPaymentType('currency', product, paymentType) ?? DEFAULT_CURRENCY
);

/**
 * Gets whether the product in the cart has tax
 *
 * @param  {GlobalStateType} state - top-level state
 * @return true if there is tax
 */
export const getCartProductIsVat = createSelector(
  getCartProduct,
  getPaymentType,
  (product, paymentType): boolean | undefined =>
    getProductFieldByPaymentType('isVat', product, paymentType)
);

/**
 * @param  {GlobalStateType} state - top-level state
 * @return price of cart product, defaulted to {@link DEFAULT_PRICE}.
 */
export const getCartProductPrice = createSelector(
  getCartProduct,
  getPaymentType,
  (product, paymentType): number =>
    getProductFieldByPaymentType('price', product, paymentType) ?? DEFAULT_PRICE
);

/**
 * Gets the purchase type of product currently in the cart
 *
 * @param  {GlobalStateType} state - top-level state
 * @return Purchase type of product entity currently in the cart. Returns null if no product is in the cart.
 */
export const getCartProductPurchaseType = createSelector(
  getCartProduct,
  (product): PurchaseType | null => (product ? product.purchaseType : null)
);

/**
 * Gets whether the product in the cart requires a zip code
 *
 * @param  {GlobalStateType} state - top-level state
 * @return true if a zip code is required
 */
export const getCartProductRequiresZip = createSelector(
  getCartProduct,
  getPaymentType,
  (product, paymentType): boolean | undefined =>
    getProductFieldByPaymentType('requireZip', product, paymentType)
);

/**
 * Gets cart product skuId
 * Note: The incentive product does not have skuId
 *
 * @param  {GlobalStateType} state - top-level state
 * @return skuId of cart product
 */
export const getCartProductSkuId = createSelector(
  getCartProduct,
  getHasIncentiveIntroPriceProduct,
  getPaymentType,
  (product, hasIncentiveProduct, paymentType): string | undefined => {
    if (hasIncentiveProduct) {
      return product?.productId;
    }
    return findProductPaymentTypeField('skuId', product, paymentType);
  }
);

/**
 * Gets the supported payment methods and if saved, their associated billing info
 *
 * @param  {GlobalStateType} state - top-level state
 * @return supported payment methods
 */
export const getPaymentMethods = createSelector(
  selectPurchase,
  (purchase): PaymentMethod[] => purchase.paymentMethods || []
);

const createGetSavedPaymentMethod = (type: string) =>
  createSelector(getPaymentMethods, (paymentMethods): PaymentMethod | undefined =>
    paymentMethods.find(
      ({ billingInfo, paymentMethod }: PaymentMethod): boolean =>
        paymentMethod === type && !isEmpty(billingInfo)
    )
  );

/**
 * Gets saved credit card data
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {PaymentMethod} credit card data object
 */
export const getSavedCC = createGetSavedPaymentMethod(PAYMENT_TYPE_CARD);

/**
 * Gets whether or not user has a saved credit card
 *
 * @param  {GlobalStateType} state - top-level state
 * @return true if user has a saved credit card
 */
export const getHasSavedCC = createSelector(getSavedCC, (cc): boolean => !!cc?.billingInfo);

/**
 * Gets saved Paypal data
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {PaymentMethod} PayPal data object
 */
export const getSavedPayPal = createGetSavedPaymentMethod(PAYMENT_TYPE_PAYPAL);

/**
 * Gets whether or not user has a saved Paypal account
 *
 * @param  {GlobalStateType} state - top-level state
 * @return true if user has a saved Paypal account
 */
export const getHasSavedPayPal = createSelector(
  getSavedPayPal,
  (paypal): boolean => !!paypal?.billingInfo
);

/**
 * Gets whether or not user has a saved payment method
 *
 * @param  {GlobalStateType} state - top-level state
 * @return true if user has a saved payment method
 */
export const getHasSavedPaymentMethod = createSelector(
  getPaymentMethods,
  (paymentMethods: PaymentMethod[]): boolean =>
    paymentMethods.reduce(
      (accumulator, paymentMethod) => accumulator || !!paymentMethod.billingInfo,
      false
    )
);

/**
 * Gets saved credit card meta data
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {CardMetaData} card metadata object
 */
export const getSavedCCMetadata = createSelector(getSavedCC, (card) => card?.cardMetadata);

/**
 * Gets email associated with saved Paypal account
 *
 * @param  {GlobalStateType} state - top-level state
 */
export const getSavedPayPalEmail = createSelector(
  getSavedPayPal,
  (paypal): string | undefined => paypal?.billingInfo?.email
);

/**
 * Gets if the payment methods fetch is in progress
 *
 * @param  {GlobalStateType} state - top-level state
 * @return true if payment methods fetch hasn't resolved yet
 */
export const getIsLoadingPaymentMethods = createSelector(
  selectPurchase,
  (purchase): boolean => purchase.isGettingPaymentMethods ?? false
);

/**
 * Gets the latest saved credit card
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entity currently in the cart
 */
export const getSavedCard = createSelector(
  selectPurchase,
  getSavedCCMetadata,
  (purchase, metadata): Card | null => metadata || purchase.latest?.card
);

/**
 * Gets the last four digits of the latest credit card
 *
 * @param  {GlobalStateType} state - top-level state
 * @return product entity currently in the cart
 */
export const getSavedCardLastFour = createSelector(
  getSavedCard,
  getSavedCCMetadata,
  (card, savedCCMetaData): string | undefined =>
    getLeverValue(LEVER_PURCHASE_SHOW_PAYMENT_OPTIONS)
      ? savedCCMetaData?.number
      : card?.number || ''
);

/**
 * Gets the variant of the latest credit card
 *
 * @param  {GlobalStateType} state - top-level state
 * @return credit card variant
 */
export const getSavedCardVariant = createSelector(
  selectPurchase,
  getSavedCCMetadata,
  (purchase, savedCCMetaData): string => savedCCMetaData?.variant || purchase.latest?.variant || ''
);

/**
 * Gets whether or not the credit card requires a zip code for use
 *
 * @param  {GlobalStateType} state - top-level state
 * @return true if card requires a zip
 */
export const getCCZipRequirement = createSelector(
  getPaymentMethods,
  (paymentMethods): boolean | undefined =>
    paymentMethods.find(({ paymentMethod }) => paymentMethod === PAYMENT_TYPE_CARD)?.requireZip
);

/**
 * Gets the confirmation info for the latest purchase
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Object} purchase info
 */
export const getPurchaseConfirmation = createSelector(
  selectPurchase,
  (purchase) => purchase.purchaseInfo
);

/**
 * Gets the email address for the purchase confirmation
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} product entity currently in the cart
 */
export const getPurchaseConfirmationEmail = createSelector(
  selectPurchase,
  (purchase) => purchase.purchaseInfo?.email
);

/**
 * Gets the tax rate for the latest purchase
 *
 * @param  {GlobalStateType} state - top-level state
 * @return tax rate for the in-progress purchase
 */
export const getPurchaseTaxRate = createSelector(
  selectPurchase,
  (purchase): number | null => purchase?.taxRate
);

/**
 * Gets the tax amount for the latest purchase
 *
 * @param  {GlobalStateType} state - top-level state
 * @return tax amount for the in-progress purchase
 */
export const getPurchaseTaxPrice = createSelector(
  selectPurchase,
  (purchase): number | null => purchase.taxPrice
);

/**
 * Gets the final amount including tax for the latest purchase
 *
 * @param  {GlobalStateType} state - top-level state
 * @return final total for the in-progress purchase
 */
export const getPurchaseFinalPrice = createSelector(
  selectPurchase,
  (purchase): number | null => purchase.finalPrice
);

/**
 * Gets the ID of the web subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return subscription ID
 */
export const getWebSubscriptionId = createSelector(
  selectPurchase,
  (purchase): string | undefined => purchase.tieredPurchases?.id
);

/**
 * Gets the expiration date of the web subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} expiration date
 */
export const getWebSubscriptionExpirationDate = createSelector(
  selectPurchase,
  (purchase): string | undefined => purchase.tieredPurchases?.expire_date
);

export const getWebSubscriptionPendingSubOfferDiscount = createSelector(
  selectPurchase,
  (purchase): number | undefined =>
    purchase.tieredPurchases?.pending_sub_offer?.offer_discount_percentage
);

/**
 * Gets the expiration date of the boost subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return expiration date
 */
export const getBoostSubscriptionExpirationDate = createSelector(
  selectPurchase,
  (purchase): string | undefined =>
    purchase.alternativeSubPurchases?.boost_subscription?.expire_date
);

/**
 * Gets the expiration date of the Tinder Select subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return expiration date of Tinder Select sub
 */
export const getSelectSubscriptionExpirationDate = createSelector(
  selectPurchase,
  (purchase): string | undefined =>
    purchase.alternativeSubPurchases?.select_subscription?.expire_date
);

/**
 * Gets the restore token of the web subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return restore token
 */
export const getWebSubscriptionRestoreToken = createSelector(
  selectPurchase,
  (purchase): string | null => purchase.restoreToken
);

/**
 * Gets the auto-renew preference on the subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return whether the subscription is set to auto-renew
 */
export const getWebSubscriptionIsAutoRenewing = createSelector(
  selectPurchase,
  (purchase): boolean | undefined => purchase.tieredPurchases?.is_auto_renewing
);

/**
 * Gets whether user's subscription is a gift card subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return whether user's subscription is a gift card subscription
 */
export const getHasGiftCardSubscription = createSelector(
  selectPurchase,
  (purchase): boolean => purchase.tieredPurchases?.platform === PLATFORM_TYPE_GIFT_CARD
);

/**
 * Gets the auto-renew preference on the boost subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return whether the boost subscription is set to auto-renew
 */
export const getBoostSubscriptionIsAutoRenewing = createSelector(
  selectPurchase,
  (purchase): boolean | undefined =>
    purchase.alternativeSubPurchases?.boost_subscription?.is_auto_renewing
);

/**
 * Gets the auto-renew preference on the Tinder Select subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return whether the Tinder Select subscription is set to auto-renew
 */
export const getSelectSubscriptionIsAutoRenewing = createSelector(
  selectPurchase,
  (purchase): boolean | undefined =>
    purchase.alternativeSubPurchases?.select_subscription?.is_auto_renewing
);

/**
 * Gets the type of the product the user has purchased
 *
 * @param  {GlobalStateType} state - top-level state
 * @return purchased product type
 */
export const getPurchasedProductType = createSelector(
  selectPurchase,
  (purchase): string | undefined => purchase.tieredPurchases?.product_type
);

/**
 * Gets the original price of the intro price web subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return original price
 */
export const getWebSubscriptionOriginalPrice = createSelector(
  selectPurchase,
  (purchase): number | undefined => purchase.tieredPurchases?.original_price
);

/**
 * Gets the payment method of the tiered subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return payment method
 */
export const getTieredSubscriptionPaymentMethod = createSelector(
  selectPurchase,
  (purchase): SubscriptionPaymentMethodType | undefined =>
    subscriptionPaymentMethodMap[purchase.tieredPurchases?.platform || '']
);

/**
 * Gets the payment method of the boost subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return payment method
 */
export const getBoostSubscriptionPaymentMethod = createSelector(
  selectPurchase,
  (purchase): SubscriptionPaymentMethodType | undefined =>
    subscriptionPaymentMethodMap[
      purchase.alternativeSubPurchases?.boost_subscription?.platform || ''
    ]
);

/**
 * Gets the payment method of the Tinder Select subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return payment method of Tinder Select sub
 */
export const getSelectSubscriptionPaymentMethod = createSelector(
  selectPurchase,
  (purchase): SubscriptionPaymentMethodType | undefined =>
    subscriptionPaymentMethodMap[
      purchase.alternativeSubPurchases?.select_subscription?.platform || ''
    ]
);

/**
 * Gets if a saved payment method is associated with the tiered subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return true when sub is paid for with saved payment method
 */
export const getHasSavedAssociatedPaymentForTieredSubscription = createSelector(
  [selectPurchase, getTieredSubscriptionPaymentMethod, getSavedCard, getHasSavedPayPal],
  (purchase, tieredSubscriptionPaymentMethod, savedCard, hasSavedPayPal): boolean => {
    const savedPaymentMap = {
      [PAYMENT_TYPE_CARD]: savedCard,
      [PAYMENT_TYPE_PAYPAL]: hasSavedPayPal,
    };
    return !!savedPaymentMap[tieredSubscriptionPaymentMethod ?? ''];
  }
);

/**
 * Gets if a saved payment method is associated with the boost subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return true when sub is paid for with saved payment method
 */
export const getHasSavedAssociatedPaymentForBoostSubscription = createSelector(
  [selectPurchase, getBoostSubscriptionPaymentMethod, getSavedCard, getHasSavedPayPal],
  (purchase, boostSubscriptionPaymentMethod, savedCard, hasSavedPayPal): boolean => {
    const savedPaymentMap = {
      [PAYMENT_TYPE_CARD]: savedCard,
      [PAYMENT_TYPE_PAYPAL]: hasSavedPayPal,
    };
    return !!savedPaymentMap[boostSubscriptionPaymentMethod ?? ''];
  }
);

/**
 * Gets if a saved payment method is associated with the Tinder Select subscription
 *
 * @param  {GlobalStateType} state - top-level state
 * @return true when sub is paid for with saved payment method
 */
export const getHasSavedAssociatedPaymentForSelectSubscription = createSelector(
  [selectPurchase, getSelectSubscriptionPaymentMethod, getSavedCard, getHasSavedPayPal],
  (purchase, selectSubscriptionPaymentMethod, savedCard, hasSavedPayPal): boolean => {
    const savedPaymentMap = {
      [PAYMENT_TYPE_CARD]: savedCard,
      [PAYMENT_TYPE_PAYPAL]: hasSavedPayPal,
    };
    return !!savedPaymentMap[selectSubscriptionPaymentMethod ?? ''];
  }
);

/**
 * Gets whether the subscription was restored
 *
 * @param  {GlobalStateType} state - top-level state
 * @return true when the subscription was restored
 */
export const getIsSubscriptionRestored = createSelector(
  selectPurchase,
  (purchase): boolean => purchase.restoreAccountSuccessConfirmation ?? false
);

/**
 * Gets whether the email address was updated
 *
 * @param  {GlobalStateType} state - top-level state
 * @return true when the email was updated successfully
 */
export const getIsEmailUpdated = createSelector(
  selectPurchase,
  (purchase): boolean => purchase.updateEmailSuccessConfirmation ?? false
);

/**
 * Gets whether the credit card was updated
 *
 * @param  {GlobalStateType} state - top-level state
 * @return true when the card was updated successfully
 */
export const getIsCardUpdated = createSelector(
  selectPurchase,
  (purchase): boolean => purchase.cardUpdateSuccess ?? false
);

/**
 * Gets whether we're submitting a purchase
 *
 * @param  {GlobalStateType} state - top-level state
 * @return whether or noz a purchase is currently processing
 */
export const getIsSubmittingPurchase = createSelector(
  selectPurchase,
  (purchase): boolean => purchase.submissionInProgress ?? false
);

/**
 * Gets whether we're submitting a purchase
 *
 * @param  {GlobalStateType} state - top-level state
 * @return whether or noz a purchase is currently processing
 */
export const getIsTaxInProgress = createSelector(
  selectPurchase,
  (purchase): boolean => purchase.getTaxInProgress ?? false
);

/**
 * Gets the pa_request for 3DS transactions
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} pa request
 */
export const getPaReq = createSelector(
  selectPurchase,
  (purchase) => purchase.purchaseInfo?.pa_request ?? ''
);

/**
 * Gets the pid for 3DS transactions
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} pid
 */
export const getPid = createSelector(
  selectPurchase,
  (purchase) =>
    purchase.purchaseInfo?.tinder_purchase_id ||
    purchase.purchaseInfo?.identify?.tinder_purchase_id ||
    purchase.purchaseInfo?.challenge?.tinder_purchase_id ||
    ''
);

/**
 * Gets the md for 3DS transactions
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} md value
 */
export const getMD = createSelector(selectPurchase, (purchase) => purchase.purchaseInfo?.md ?? '');

/**
 * Gets the 3DS method url for 3DS transactions
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} 3DS method url
 */
export const getMethodUrl = createSelector(
  selectPurchase,
  (purchase) => purchase.purchaseInfo?.identify?.three_ds_method_url ?? ''
);

/**
 * Gets the 3DS acs trans id for 3DS transactions
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} acs_trans_id
 */
export const getAcsTransId = createSelector(
  selectPurchase,
  (purchase) => purchase.purchaseInfo?.challenge?.acs_trans_id ?? ''
);

/**
 * Gets the 3DS acs trans id for 3DS transactions
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} message version
 */
export const getMessageVersion = createSelector(
  selectPurchase,
  (purchase) => purchase.purchaseInfo?.challenge?.message_version ?? ''
);

/**
 * Gets the 3DS acs trans id for 3DS transactions
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} acs_url
 */
export const getAcsUrl = createSelector(
  selectPurchase,
  (purchase) => purchase.purchaseInfo?.challenge?.acs_url ?? ''
);

/**
 * Gets the 3ds server transaction id for 3DS transactions
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} transaction id
 */
export const getServerTransactionId = createSelector(
  selectPurchase,
  (purchase) =>
    purchase.purchaseInfo?.identify?.three_ds_server_trans_id ||
    purchase.purchaseInfo?.challenge?.three_ds_server_trans_id ||
    ''
);

/**
 * Gets the issuer url for 3DS transactions
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} issuer url
 */
export const getIssuerUrl = createSelector(
  selectPurchase,
  (purchase) => purchase.purchaseInfo?.issuer_url ?? ''
);

/**
 * Gets the pagetype when the user hits the checkout/credit card page
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} get the page type
 */
export const getCheckoutPageType = createSelector(
  selectPurchase,
  (purchase): string => purchase.pageType
);

/**
 * Gets the page version on the checkout page
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} pageVersion
 */
export const getCheckoutPageVersion = createSelector(
  selectPurchase,
  (purchase): string => purchase.pageVersion
);

/**
 * Gets validation status of the checkout/update card form
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} true is the form is validated
 */
export const getIsFormValidated = createSelector(
  selectPurchase,
  (purchase): boolean => purchase.isFormValidated
);

/**
 * Gets the data of the checkout form
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {FormData} form data object
 */
export const getCheckoutFormData = createSelector(
  selectPurchase,
  (purchase): PurchaseFormData => purchase.formData
);

/**
 * Gets selected credit card type
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} selected credit card type
 */
export const getCCType = createSelector(selectPurchase, (purchase): string => purchase.ccType);

/**
 * Gets the 3ds type
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} get 3ds type
 */
export const get3DSType = createSelector(
  selectPurchase,
  (purchase): number => purchase.threeDSVersion
);

export const getUpsellsState = createSelector(
  selectPurchase,
  (purchase): Upsells => purchase.upsells
);

/**
 * Gets boost upsell modal last shown timestamp
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} boost modal last shown timestamp
 */
export const getBoostUpsellShownTimeStamp = createSelector(
  getUpsellsState,
  (upsells): number => upsells.boost.timestamp
);

/**
 * Gets attach a msg modal last shown timestamp
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} attach a msg modal last shown timestamp
 */
export const getAttachAMessageShownTimeStamp = createSelector(
  getUpsellsState,
  (upsells): number => upsells.attachAMessage.timestamp
);

/**
 * Gets new likes modal last shown timestamp
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} new likes modal last shown timestamp
 */
export const getNewLikesUpsellShownTimeStamp = createSelector(
  getUpsellsState,
  (upsells): number => upsells.newLikes.timestamp
);

/**
 * Gets gold home tooltip last shown timestamp
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} gold home tooltip last shown timestamp
 */
export const getGoldHomeTooltipShownTimeStamp = createSelector(
  getUpsellsState,
  (upsells): number => upsells.goldHomeTooltip.timestamp
);

/**
 * Gets home screen tooltip shown
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} home screen tooltip last shown
 */
export const getHomeScreenTooltipShown = createSelector(
  getUpsellsState,
  (upsells): boolean => upsells.homeScreenTooltip.shown
);

/**
 * Gets priority likes modal last shown timestamp
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} priority likes modal last shown timestamp
 */
export const getPriorityLikesUpsellShownTimeStamp = createSelector(
  getUpsellsState,
  (upsells): number => upsells.priorityLikes.timestamp
);

/**
 * Gets priority likes modal is shown
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} priority likes shown
 */
export const getPriorityLikesUpsellShown = createSelector(
  getUpsellsState,
  (upsells: Upsells): boolean => upsells.priorityLikes.shown
);

/**
 * Gets if superlike upsell modal is shown
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} superlike upsell modal is shown
 */
export const getSuperlikeUpsellModalShown = createSelector(
  getUpsellsState,
  (upsells): boolean => upsells.superlikeHighRsrr.shown
);

/**
 * Gets time at which superlike upsell modal is shown
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} superlike upsell modal last shown timestamp
 */
export const getSuperlikeUpsellModalTimestamp = createSelector(
  getUpsellsState,
  (upsells): number => upsells.superlikeHighRsrr.timestamp
);

/**
 * Gets if web discount banner should be shown
 *
 * @param  {GlobalStateType} state - top-level state
 * @return web discount banner should be shown
 */
export const getShouldShowWebDiscountBanner = createSelector(
  getUpsellsState,
  (upsells): boolean =>
    !(upsells.webDiscountBanner.shown || upsells.webDiscountBanner.stopShowingUpsell)
);

/**
 * Gets web discount banner last shown timestamp
 *
 * @param  {GlobalStateType} state - top-level state
 * @return timestamp that web discount banner is last shown
 */
export const getWebDiscountBannerTimestamp = createSelector(
  getUpsellsState,
  (upsells): number => upsells.webDiscountBanner.timestamp
);

/**
 * Gets number of times web discount banner has been shown
 *
 * @param  {GlobalStateType} state - top-level state
 * @return web discount banner number of times shown
 */
export const getWebDiscountBannerAmountShown = createSelector(
  getUpsellsState,
  (upsells): number => upsells.webDiscountBanner.amountShown
);

/**
 * Gets whether web discount login banner has been shown
 *
 * @param  {GlobalStateType} state - top-level state
 * @return web discount login banner is shown
 */
export const getWebDiscountLoginBannerShown = createSelector(
  getUpsellsState,
  (upsells): boolean => upsells.webDiscountLoginBanner.shown
);

/**
 * Gets whether web discount in app banner has been shown/interacted with
 *
 * @param  {GlobalStateType} state - top-level state
 * @return web discount in app banner is shown
 */
export const selectWebDiscountInAppBannerShown = createSelector(
  getUpsellsState,
  (upsells): boolean => upsells.webDiscountBanner.shown
);

/**
 * Gets time at which primetimeboost upsell modal is shown
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} primetimeboost upsell modal amount shown
 */
export const getPrimetimeBoostUpsellModalAmountShown = createSelector(
  getUpsellsState,
  (upsells): number => upsells.primetimeboost.amountShown
);

/**
 * Gets times of which primetimeboost upsell modal is shown
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} primetimeboost upsell modal last shown timestamp
 */
export const getPrimetimeBoostUpsellModalTimestamp = createSelector(
  getUpsellsState,
  (upsells): number => upsells.primetimeboost.timestamp
);

/**
 * Gets save card analytics number
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {number} number for save card analytics
 */
export const getSaveCardBitwise = createSelector(
  selectPurchase,
  (purchase): number | null => purchase.saveCardBitwise
);

/**
 * Gets adyen device fingerprint hash for fraud detection for purchases
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} device fingerprint hash
 */
export const getAdyenDeviceFingerprint = createSelector(
  selectPurchase,
  (purchase): string | null => purchase.adyenDeviceFingerprint
);

/**
 * Gets braintree device fingerprint hash for fraud detection for purchases
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {string} device fingerprint hash
 */
export const getBraintreeDeviceFingerprint = createSelector(
  selectPurchase,
  (purchase): string | null => purchase.braintreeDeviceFingerprint
);

/**
 * Gets braintree client token for braintree purchases
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {Client} client token
 */
export const getBraintreeClientToken = createSelector(
  selectPurchase,
  (purchase): Client | null => purchase.braintreeClientToken
);

/**
 * Gets whether there is a braintree sdk error
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} has braintree sdk error
 */
export const getHasBraintreeError = createSelector(
  selectPurchase,
  (purchase): boolean => purchase.braintreeError
);

/**
 * Gets whether the Payment SDK is loading
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} payment sdk loading state
 */
export const getPaymentSdkIsLoading = createSelector(
  [
    selectPurchase,
    (
      _state: GlobalStateType,
      paymentType: PaymentMethodType | undefined
    ): PaymentMethodType | undefined => paymentType,
  ],
  (purchase, paymentType): PaymentMethodType | undefined =>
    purchase.isPaymentSdkLoading[paymentType || '']
);

/**
 * Gets whether the PayPal SDK is loading
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} payment sdk loading state
 */
export const getPayPalSdkIsLoading = createSelector(
  selectPurchase,
  (purchase): boolean => purchase.isPaymentSdkLoading[PAYMENT_TYPE_PAYPAL]
);

/**
 * Gets whether the Payment SDK is saving
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} payment saving state
 */
export const getPaymentIsSaving = createSelector(
  [
    selectPurchase,
    (
      _state: GlobalStateType,
      paymentType: PaymentMethodType | undefined
    ): PaymentMethodType | undefined => paymentType,
  ],
  (purchase, paymentType) => purchase.isPaymentSaving[paymentType || '']
);

/**
 * Gets whether the purchase is from any Incognito entrypoint
 *
 * @param  {GlobalStateType} state - top-level state
 * @return {boolean} whether the purchase is from any Incognito entrypoint
 */
export const getPurchasedFromIncognito = createSelector(
  selectPurchase,
  (purchase): boolean => purchase.purchasedFromIncognito
);
